import { FC, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { toast } from 'react-toastify';
import { Button, Input, Spinner } from 'reactstrap';
import GoBack from '../../components/GoBack';
import { AI_REFERENCE_QUESTIONS } from '../../constants';
import { ADMIN_ROUTE } from '../../utils/constant';
import {
  ADD_AI_REFERENCE_QUESTION,
  AI_REFERENCE_QUESTION_TEMPLATES,
  DELETE_AI_REFERENCE_QUESTION,
  REORDER_AI_REFERENCE_QUESTIONS,
  UPDATE_AI_REFERENCE_QUESTION,
} from './gql';

const AMBER = '#eaa827';

// Wire values are the GraphQL `AiReferenceFieldKeyEnum` member NAMES (uppercase),
// not the DB values. Declared locally like the starter-questions page — this app
// has no GraphQL codegen.
type AiReferenceFieldKey =
  | 'RELATIONSHIP_TO_CANDIDATE'
  | 'PART_OF_INTERVIEW_PROCESS_TO_HIRE'
  | 'MAIN_DUTY'
  | 'CANDIDATE_STRENGTH'
  | 'IMPROVEMENT'
  | 'SOFTWARE_USED'
  | 'PERSONALITY'
  | 'ELIGIBLE_FOR_REHIRE'
  | 'ADDITIONAL_COMMENTS';

// The nine legacy Reference PDF fields a question can fill. "None" (empty value)
// = custom question, rendered as an extra row on the PDF.
const FIELD_KEY_OPTIONS: { value: AiReferenceFieldKey; label: string }[] = [
  { value: 'RELATIONSHIP_TO_CANDIDATE', label: 'Relationship to the candidate' },
  { value: 'PART_OF_INTERVIEW_PROCESS_TO_HIRE', label: 'Part of the interview process to hire' },
  { value: 'MAIN_DUTY', label: 'Main job duties/responsibilities' },
  { value: 'CANDIDATE_STRENGTH', label: "Candidate's strengths" },
  { value: 'IMPROVEMENT', label: 'Improvement' },
  { value: 'SOFTWARE_USED', label: 'Computer programs/software' },
  { value: 'PERSONALITY', label: 'Personality' },
  { value: 'ELIGIBLE_FOR_REHIRE', label: 'Eligible for rehire' },
  { value: 'ADDITIONAL_COMMENTS', label: 'Additional comments' },
];

const fieldKeyLabel = (key: AiReferenceFieldKey | null) =>
  FIELD_KEY_OPTIONS.find((o) => o.value === key)?.label ?? null;

interface ReferenceQuestion {
  id: string;
  label: string;
  prompt: string;
  fieldKey: AiReferenceFieldKey | null;
  sortOrder: number;
  isActive: boolean;
}

interface Draft {
  label: string;
  prompt: string;
  fieldKey: AiReferenceFieldKey | '';
  isActive: boolean;
}

const EMPTY_DRAFT: Draft = { label: '', prompt: '', fieldKey: '', isActive: true };

const textareaStyle: React.CSSProperties = {
  width: '100%', border: '1px solid #80bdff', borderRadius: 4, padding: '6px 8px', resize: 'vertical',
};

// ── Shared editor (add + edit) ────────────────────────────────────────────────
interface EditorProps {
  idPrefix: string;
  draft: Draft;
  onChange: (d: Draft) => void;
  // fieldKeys already mapped by OTHER rows — disabled in the dropdown so the
  // admin sees the conflict before the server rejects it.
  takenFieldKeys: Set<AiReferenceFieldKey>;
}

const QuestionEditor: FC<EditorProps> = ({ idPrefix, draft, onChange, takenFieldKeys }) => (
  <>
    <label htmlFor={`${idPrefix}-label`} style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 500 }}>Label (shown on the PDF)</label>
    <Input
      id={`${idPrefix}-label`}
      value={draft.label}
      onChange={(e) => onChange({ ...draft, label: e.target.value })}
      placeholder="e.g. Personality"
      autoFocus
    />
    <label htmlFor={`${idPrefix}-prompt`} style={{ margin: '8px 0 4px', fontSize: 13, fontWeight: 500 }}>
      Prompt (what the voice agent asks — {'{{candidateName}}'} is replaced with the candidate&apos;s name)
    </label>
    <textarea
      id={`${idPrefix}-prompt`}
      value={draft.prompt}
      onChange={(e) => onChange({ ...draft, prompt: e.target.value })}
      placeholder="Type the question the AI should ask the reference..."
      rows={3}
      style={textareaStyle}
    />
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <label htmlFor={`${idPrefix}-fieldKey`} style={{ margin: 0, fontSize: 13, fontWeight: 500 }}>PDF field:</label>
        <Input
          id={`${idPrefix}-fieldKey`}
          type="select"
          value={draft.fieldKey}
          onChange={(e) => onChange({ ...draft, fieldKey: e.target.value as AiReferenceFieldKey | '' })}
          style={{ width: 280 }}
        >
          <option value="">None (custom question)</option>
          {FIELD_KEY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value} disabled={takenFieldKeys.has(o.value)}>
              {o.label}{takenFieldKeys.has(o.value) ? ' (in use)' : ''}
            </option>
          ))}
        </Input>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Input
          id={`${idPrefix}-active`}
          type="checkbox"
          checked={draft.isActive}
          onChange={(e) => onChange({ ...draft, isActive: e.target.checked })}
          style={{ margin: 0, position: 'static' }}
        />
        <label htmlFor={`${idPrefix}-active`} style={{ margin: 0, fontSize: 13, fontWeight: 500 }}>Active</label>
      </div>
    </div>
  </>
);

// ── Single sortable row ───────────────────────────────────────────────────────
interface RowProps {
  question: ReferenceQuestion;
  index: number;
  editingId: string | null;
  editingDraft: Draft;
  takenFieldKeys: Set<AiReferenceFieldKey>;
  onEditDraftChange: (d: Draft) => void;
  onEditStart: (q: ReferenceQuestion) => void;
  onEditSave: (id: string) => void;
  onEditCancel: () => void;
  onToggleActive: (q: ReferenceQuestion) => void;
  onDelete: (id: string) => void;
}

const SortableRow: FC<RowProps> = ({
  question: q,
  index,
  editingId,
  editingDraft,
  takenFieldKeys,
  onEditDraftChange,
  onEditStart,
  onEditSave,
  onEditCancel,
  onToggleActive,
  onDelete,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: q.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    marginBottom: 12,
    padding: '12px 14px',
    border: `1px solid ${isDragging ? AMBER : '#dee2e6'}`,
    borderRadius: 6,
    background: isDragging ? '#fffbf0' : q.isActive ? '#fff' : '#f8f9fa',
    boxShadow: isDragging ? '0 4px 12px rgba(0,0,0,0.12)' : 'none',
    zIndex: isDragging ? 999 : 'auto',
    position: 'relative',
  };

  const isEditing = editingId === q.id;
  const mapped = fieldKeyLabel(q.fieldKey);

  return (
    <div ref={setNodeRef} style={style}>
      {/* Drag handle */}
      <div
        {...listeners}
        {...attributes}
        title="Drag to reorder"
        style={{
          cursor: 'grab', color: '#adb5bd', padding: '2px 4px', fontSize: 18,
          lineHeight: 1, userSelect: 'none', flexShrink: 0, alignSelf: 'center',
        }}
      >
        ⠿
      </div>

      <span style={{ minWidth: 26, fontWeight: 600, color: '#6c757d', paddingTop: 2, flexShrink: 0 }}>
        {index + 1}.
      </span>

      <div style={{ flex: 1, opacity: !isEditing && !q.isActive ? 0.6 : 1 }}>
        {isEditing ? (
          <QuestionEditor
            idPrefix={`edit-${q.id}`}
            draft={editingDraft}
            onChange={onEditDraftChange}
            takenFieldKeys={takenFieldKeys}
          />
        ) : (
          <>
            <div style={{ fontWeight: 600 }}>{q.label}</div>
            <div style={{ lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{q.prompt}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
              <span
                style={{
                  fontSize: 11, fontWeight: 600, letterSpacing: 0.3, padding: '2px 8px', borderRadius: 10,
                  ...(mapped
                    ? { background: '#e7f1ff', color: '#0d6efd', border: '1px solid #b6d4fe' }
                    : { background: '#f1f3f5', color: '#495057', border: '1px solid #dee2e6' }),
                }}
              >
                {mapped ? `PDF: ${mapped}` : 'Custom question'}
              </span>
              {!q.isActive && (
                <span
                  style={{
                    fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3,
                    padding: '2px 8px', borderRadius: 10,
                    background: '#fff3cd', color: '#856404', border: '1px solid #ffeeba',
                  }}
                >
                  Inactive
                </span>
              )}
            </div>
          </>
        )}
      </div>

      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        {isEditing ? (
          <>
            <Button size="sm" color="success" onClick={() => onEditSave(q.id)}>Save</Button>
            <Button size="sm" color="secondary" onClick={onEditCancel}>Cancel</Button>
          </>
        ) : (
          <>
            <Button size="sm" color="outline-secondary" onClick={() => onToggleActive(q)}>
              {q.isActive ? 'Deactivate' : 'Activate'}
            </Button>
            <Button size="sm" color="outline-secondary" onClick={() => onEditStart(q)}>Edit</Button>
            <Button size="sm" color="outline-danger" onClick={() => onDelete(q.id)}>✕</Button>
          </>
        )}
      </div>
    </div>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────
const AiReferenceQuestions = () => {
  const [questions, setQuestions] = useState<ReferenceQuestion[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingDraft, setEditingDraft] = useState<Draft>(EMPTY_DRAFT);
  const [addingNew, setAddingNew] = useState(false);
  const [newDraft, setNewDraft] = useState<Draft>(EMPTY_DRAFT);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const { loading, refetch } = useQuery(AI_REFERENCE_QUESTION_TEMPLATES, {
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => setQuestions(data?.aiReferenceQuestionTemplates ?? []),
  });

  const [addQuestion, { loading: adding }] = useMutation(ADD_AI_REFERENCE_QUESTION, {
    onCompleted: (data) => {
      setQuestions((prev) => [...prev, data.addAiReferenceQuestion]);
      setAddingNew(false);
      setNewDraft(EMPTY_DRAFT);
    },
    onError: (err) => toast.error(err.message),
  });

  const [updateQuestion, { loading: updating }] = useMutation(UPDATE_AI_REFERENCE_QUESTION, {
    onCompleted: (data) => {
      const updated = data.updateAiReferenceQuestion;
      setQuestions((prev) => prev.map((q) => (q.id === updated.id ? { ...q, ...updated } : q)));
      setEditingId(null);
    },
    onError: (err) => toast.error(err.message),
  });

  const [deleteQuestion] = useMutation(DELETE_AI_REFERENCE_QUESTION, {
    onError: (err) => toast.error(err.message),
  });

  const [reorderQuestions] = useMutation(REORDER_AI_REFERENCE_QUESTIONS, {
    onCompleted: (data) => setQuestions(data?.reorderAiReferenceQuestions ?? []),
    onError: (err) => {
      toast.error(err.message);
      refetch();
    },
  });

  useEffect(() => {
    document.title = 'AI Reference Questions - RealREPP';
  }, []);

  // fieldKeys mapped by rows other than `selfId`.
  const takenFieldKeysExcept = (selfId: string | null) =>
    new Set(
      questions
        .filter((q) => q.id !== selfId && q.fieldKey)
        .map((q) => q.fieldKey as AiReferenceFieldKey),
    );

  const draftIsValid = (d: Draft) => !!d.label.trim() && !!d.prompt.trim();

  const handleSaveEdit = (id: string) => {
    if (!draftIsValid(editingDraft)) {
      toast.error('Label and prompt are required');
      return;
    }
    updateQuestion({
      variables: {
        id,
        label: editingDraft.label.trim(),
        prompt: editingDraft.prompt.trim(),
        // Explicit null clears the mapping server-side ("None").
        fieldKey: editingDraft.fieldKey || null,
        isActive: editingDraft.isActive,
      },
    });
  };

  const handleToggleActive = (q: ReferenceQuestion) => {
    if (updating) return;
    // fieldKey omitted on purpose → the server leaves the mapping unchanged.
    updateQuestion({ variables: { id: q.id, isActive: !q.isActive } });
  };

  const handleAdd = () => {
    if (!draftIsValid(newDraft)) {
      toast.error('Label and prompt are required');
      return;
    }
    addQuestion({
      variables: {
        label: newDraft.label.trim(),
        prompt: newDraft.prompt.trim(),
        fieldKey: newDraft.fieldKey || null,
        isActive: newDraft.isActive,
      },
    });
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(
      'Delete this question from the AI Reference Taker question list? References already completed keep their answers. Tip: deactivate instead to stop asking it without deleting it.',
    )) {
      return;
    }
    // With onError set, Apollo resolves (not rejects) on failure — only drop the
    // row when the server confirmed the delete.
    const res = await deleteQuestion({ variables: { id } });
    if (res?.data?.deleteAiReferenceQuestion) {
      setQuestions((prev) => prev.filter((q) => q.id !== id));
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = questions.findIndex((q) => q.id === active.id);
    const newIndex = questions.findIndex((q) => q.id === over.id);
    const reordered = arrayMove(questions, oldIndex, newIndex);
    setQuestions(reordered);
    reorderQuestions({ variables: { ids: reordered.map((q) => q.id) } });
  };

  return (
    <div className="admin-tabs team-tabs">
      <GoBack url={ADMIN_ROUTE} />

      <div className="primaryHeading">
        <h5 className="m-0">{AI_REFERENCE_QUESTIONS}</h5>
      </div>

      <div style={{ padding: '16px 4px', maxWidth: 900 }}>
        <p style={{ color: '#6c757d', fontSize: 13 }}>
          These are the questions the AI Reference Taker asks every reference contact, in this order. Drag to
          reorder. The <strong>label</strong> is what appears on the reference PDF; the <strong>prompt</strong> is
          what the voice agent says. Map a question to a <strong>PDF field</strong> to fill that section of the
          existing reference document; questions set to <strong>None</strong> are added as extra rows. Inactive
          questions are kept but not asked. Changes apply to calls placed after you save — answers already
          collected are not affected.
        </p>

        {loading && questions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spinner /></div>
        ) : (
          <>
            {questions.length === 0 && !addingNew && (
              <p style={{ color: '#6c757d', fontStyle: 'italic' }}>
                No reference questions yet. Add one below.
              </p>
            )}

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={questions.map((q) => q.id)} strategy={verticalListSortingStrategy}>
                {questions.map((q, index) => (
                  <SortableRow
                    key={q.id}
                    question={q}
                    index={index}
                    editingId={editingId}
                    editingDraft={editingDraft}
                    takenFieldKeys={takenFieldKeysExcept(q.id)}
                    onEditDraftChange={setEditingDraft}
                    onEditStart={(item) => {
                      setEditingId(item.id);
                      setEditingDraft({
                        label: item.label,
                        prompt: item.prompt,
                        fieldKey: item.fieldKey ?? '',
                        isActive: item.isActive,
                      });
                    }}
                    onEditSave={handleSaveEdit}
                    onEditCancel={() => setEditingId(null)}
                    onToggleActive={handleToggleActive}
                    onDelete={handleDelete}
                  />
                ))}
              </SortableContext>
            </DndContext>

            {/* Add new */}
            {addingNew ? (
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12,
                padding: '12px 14px', border: '1px dashed #80bdff', borderRadius: 6, background: '#f0f7ff',
              }}>
                <div style={{ flex: 1 }}>
                  <QuestionEditor
                    idPrefix="new-question"
                    draft={newDraft}
                    onChange={setNewDraft}
                    takenFieldKeys={takenFieldKeysExcept(null)}
                  />
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <Button size="sm" color="success" disabled={adding} onClick={handleAdd}>Save</Button>
                  <Button size="sm" color="secondary" onClick={() => { setAddingNew(false); setNewDraft(EMPTY_DRAFT); }}>Cancel</Button>
                </div>
              </div>
            ) : (
              <Button style={{ borderColor: AMBER, color: AMBER }} outline size="sm" onClick={() => setAddingNew(true)}>
                + Add Reference Question
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AiReferenceQuestions;
